// Step 19 — pure helpers for intelligence-workspace prioritisation
// and label mapping. Deterministic, side-effect-free, no fake data.
//
// Rules:
//   - Never invent a confidence percentage. Risk labels map from the
//     integer 1..5 scores that the WorkspaceRiskProfile already stores
//     (see backend/valuation/models.py); anything outside that range
//     is labelled "Okänd".
//   - Priority ordering is deterministic and explainable — see the
//     comment on `attentionRank`.
//   - Every finding produced by these helpers carries the underlying
//     row's ID (or reason ID list) so the UI can link back to the
//     domain object.

export const KIND = {
  RISK: "risk",
  PREPAREDNESS: "preparedness",
  BUYER_SIGNAL: "buyer_signal",
  CONTRACT_UNCERTAINTY: "contract_uncertainty",
  CONTRACT_REVIEW: "contract_review",
  ACTION_PENDING: "action_pending",
  SUGGESTED_ACTION: "suggested_action",
};

export const SEVERITY = {
  HIGH: "high",
  MEDIUM: "medium",
  LOW: "low",
  UNKNOWN: "unknown",
};

export function riskScoreToSeverity(score) {
  // Backend stores 0..5 where 0 = "unknown/not assessed" per
  // WorkspaceRiskProfile.
  const s = Number(score);
  if (!Number.isFinite(s) || s <= 0) return SEVERITY.UNKNOWN;
  if (s >= 4) return SEVERITY.HIGH;
  if (s === 3) return SEVERITY.MEDIUM;
  return SEVERITY.LOW;
}

export function severityLabel(severity) {
  switch (severity) {
    case SEVERITY.HIGH: return "Hög";
    case SEVERITY.MEDIUM: return "Medel";
    case SEVERITY.LOW: return "Låg";
    default: return "Okänd";
  }
}

const SEVERITY_ORDER = {
  [SEVERITY.HIGH]: 3,
  [SEVERITY.MEDIUM]: 2,
  [SEVERITY.LOW]: 1,
  [SEVERITY.UNKNOWN]: 0,
};

// Priority ordering for the attention queue. Lower rank = show first.
// Deterministic: never involves random noise or fabricated confidence.
const KIND_WEIGHT = {
  [KIND.RISK]: 100,
  [KIND.PREPAREDNESS]: 80,
  [KIND.CONTRACT_REVIEW]: 70,
  [KIND.CONTRACT_UNCERTAINTY]: 60,
  [KIND.ACTION_PENDING]: 55,
  [KIND.BUYER_SIGNAL]: 40,
  [KIND.SUGGESTED_ACTION]: 30,
};

export function attentionRank(item) {
  // Higher score → earlier in queue.
  const kindW = KIND_WEIGHT[item.kind] || 0;
  const sevW = (SEVERITY_ORDER[item.severity] || 0) * 20;
  return kindW + sevW;
}

// ── Extractors ─────────────────────────────────────────────────────
// Each extractor turns a subset of the raw API payload into normalized
// finding objects: {kind, severity, title, summary, category, source,
// evidence, ask_prompt, link}. The list is deterministic and includes
// only rows the underlying data can support — no fabrication.

function _kv(section, field) {
  return `${section}.${field}`;
}

export function extractRiskFindings(dealSnapshot) {
  const risks = dealSnapshot?.sections?.known_risks || {};
  const out = [];
  for (const [name, fact] of Object.entries(risks)) {
    if (!fact || !fact.known) continue;
    const severity = riskScoreToSeverity(fact.value);
    out.push({
      kind: KIND.RISK,
      severity,
      title: _humanize(name),
      summary: `Registrerad risk-nivå: ${fact.value}${fact.unit || ""}.`,
      category: "Risk",
      // The evidence for a risk score IS the risk profile row itself.
      source: fact.source || null,
      evidence: fact.source
        ? [{ kind: "risk_profile", label: "Riskprofil", provenance: fact.source }]
        : [],
      ask_prompt:
        `Förklara risknivån för "${_humanize(name)}" i denna affär och vad ` +
        `det innebär för en förvärvare.`,
      key: _kv("known_risks", name),
    });
  }
  return out;
}

export function extractPreparednessFindings(preparedness) {
  const out = [];
  for (const obs of preparedness || []) {
    if (!obs) continue;
    out.push({
      kind: KIND.PREPAREDNESS,
      severity: SEVERITY.MEDIUM,
      title: obs.label || obs.kind || "Observerat gap",
      summary: obs.detail || null,
      category: "Beredskap",
      // Preparedness observations carry their own provenance chain via
      // the deal snapshot source pointer.
      source: obs.deal_snapshot_source || null,
      evidence: (obs.raised_by_item_ids || []).slice(0, 5).map((id) => ({
        kind: "buyer_intel_item",
        label: `BuyerIntelligenceItem #${id}`,
        item_id: id,
      })),
      // Preparedness is labelled by the backend as fact_or_inference.
      fact_or_inference: obs.fact_or_inference || null,
      ask_prompt:
        `Förklara observationen "${obs.label || obs.kind}" och vad som borde ` +
        `göras för att stänga gapet.`,
      key: `preparedness.${obs.kind || ""}`,
    });
  }
  return out;
}

export function extractSuggestedActionFindings(suggestions) {
  const out = [];
  for (const s of suggestions || []) {
    if (!s) continue;
    out.push({
      kind: KIND.SUGGESTED_ACTION,
      severity: SEVERITY.LOW,
      title: s.label || s.kind || "Föreslagen åtgärd",
      summary: s.detail || null,
      category: "Förslag",
      source: null,
      evidence: [],
      // Deterministic hint — carries user_action_required from backend.
      user_action_required: !!s.user_action_required,
      ask_prompt:
        `Förklara det föreslagna arbetsflödet "${s.label || s.kind}" och ` +
        `vilka konkreta steg det innebär.`,
      key: `suggested.${s.kind || ""}`,
    });
  }
  return out;
}

export function extractBuyerFindings(portfolio) {
  const out = [];
  for (const b of portfolio || []) {
    if (!b || !b.acquirer) continue;
    // Silence signal is already a deterministic derived observation on
    // the backend — reuse it verbatim.
    if (b.silence?.is_silent) {
      out.push({
        kind: KIND.BUYER_SIGNAL,
        severity: SEVERITY.MEDIUM,
        title: `${b.acquirer.name} har varit tyst i ${b.silence.days_since_last_outbound} dagar`,
        summary: b.silence.reason || null,
        category: "Köparsignal",
        source: null,
        evidence: [],
        acquirer_id: b.acquirer.id,
        ask_prompt:
          `Vad kan förklara att ${b.acquirer.name} inte har svarat på ` +
          `${b.silence.days_since_last_outbound} dagar, och hur bör vi hantera det?`,
        key: `silence.${b.acquirer.id}`,
      });
    }
    if (b.interest_signal?.value) {
      out.push({
        kind: KIND.BUYER_SIGNAL,
        severity: SEVERITY.LOW,
        title: `${b.acquirer.name}: intressenivå ${b.interest_signal.value}`,
        summary: null,
        category: "Köparsignal",
        fact_or_inference: b.interest_signal.fact_or_inference || null,
        source: null,
        evidence: [],
        acquirer_id: b.acquirer.id,
        ask_prompt:
          `Vad vet vi om intresset från ${b.acquirer.name}, och vilka signaler ` +
          `bekräftar eller motsäger det?`,
        key: `interest.${b.acquirer.id}`,
      });
    }
  }
  return out;
}

export function extractContractFindings(contracts) {
  const out = [];
  for (const c of contracts || []) {
    if (!c) continue;
    // Uncertainties that the AI drafting flow could not resolve.
    for (const u of c.uncertainties || []) {
      out.push({
        kind: KIND.CONTRACT_UNCERTAINTY,
        severity: SEVERITY.MEDIUM,
        title: `${c.title || c.kind || "Kontrakt"}: osäkerhet`,
        summary: typeof u === "string" ? u : (u?.label || String(u)),
        category: "Kontraktsfynd",
        // The evidence for a contract uncertainty IS the draft row
        // itself (variables + clauses carry the deep provenance).
        source: {
          source_type: "contract_draft",
          source_id: String(c.id),
          label: c.title || c.kind || `Draft #${c.id}`,
        },
        evidence: [],
        contract_id: c.id,
        ask_prompt:
          `Förklara osäkerheten "${typeof u === "string" ? u : (u?.label || "")}" i ` +
          `kontraktet ${c.title || `#${c.id}`}.`,
        key: `contract.uncertainty.${c.id}.${(typeof u === "string" ? u : u?.label || "").slice(0, 40)}`,
      });
    }
    // Contracts awaiting human review are their own finding.
    if (c.status === "pending_review") {
      out.push({
        kind: KIND.CONTRACT_REVIEW,
        severity: SEVERITY.MEDIUM,
        title: `${c.title || c.kind || "Kontrakt"} väntar granskning`,
        summary: null,
        category: "Kontrakt",
        source: {
          source_type: "contract_draft",
          source_id: String(c.id),
          label: c.title || c.kind || `Draft #${c.id}`,
        },
        evidence: [],
        contract_id: c.id,
        ask_prompt: null,
        key: `contract.review.${c.id}`,
      });
    }
  }
  return out;
}

export function extractPendingActionFindings(actions) {
  const out = [];
  for (const a of actions || []) {
    if (!a) continue;
    // Actions awaiting human approval — actionable for the reviewer.
    if (a.status === "proposed" || a.status === "pending_review") {
      const severity =
        a.priority === "critical" || a.priority === "high"
          ? SEVERITY.HIGH
          : a.priority === "medium"
            ? SEVERITY.MEDIUM
            : SEVERITY.LOW;
      out.push({
        kind: KIND.ACTION_PENDING,
        severity,
        title: a.title || "Åtgärd väntar godkännande",
        summary: a.description || a.rationale || null,
        category: "Åtgärd",
        source: {
          source_type: "deal_action",
          source_id: String(a.id),
          label: a.title || `Action #${a.id}`,
        },
        evidence: [],
        action_id: a.id,
        ask_prompt: null,
        key: `action.pending.${a.id}`,
      });
    }
  }
  return out;
}

// Compose the attention queue by concatenating extractor outputs and
// sorting by `attentionRank`. Kept small — no filtering rules, no
// dedup magic; the extractors are already the source of truth for
// what counts.
export function buildAttentionQueue(
  { dealSnapshot, preparedness, portfolio, contracts, actions, suggestions } = {},
) {
  const items = [
    ...extractRiskFindings(dealSnapshot),
    ...extractPreparednessFindings(preparedness),
    ...extractBuyerFindings(portfolio),
    ...extractContractFindings(contracts),
    ...extractPendingActionFindings(actions),
    ...extractSuggestedActionFindings(suggestions),
  ];
  return items.sort((a, b) => attentionRank(b) - attentionRank(a));
}

function _humanize(s) {
  if (!s) return "";
  return String(s).replace(/_/g, " ");
}
